import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Pencil, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

// Âge/poids/taille/objectif/niveau sont désormais des questions structurées de l'OnboardingWizard
// (synchronisées avec le profil unique du Calculateur de Calories, cf. tracking_service.py) — retirées
// d'ici pour ne jamais être redemandées. Seules les questions spécifiques au coaching restent.
const FIELDS = [
  { k: 'lieu', label: ["Lieu d'entraînement", 'Training location'], type: 'select', options: ['Salle de sport', 'À la maison', 'En extérieur', 'Mixte'] },
  { k: 'materiel', label: ['Matériel disponible', 'Available equipment'], type: 'text' },
  { k: 'disponibilites', label: ['Disponibilités (jours/horaires)', 'Availability'], type: 'text' },
  { k: 'blessures', label: ['Blessures / limitations', 'Injuries / limitations'], type: 'textarea' },
  { k: 'alimentation', label: ['Préférences / allergies alimentaires', 'Food preferences / allergies'], type: 'textarea' },
  { k: 'motivation', label: ['Motivation profonde & échéance', 'Deeper motivation & deadline'], type: 'textarea' },
];
export { FIELDS };

export const OnboardingCard = ({ en, onboarding, api, onSaved }) => {
  const done = !!onboarding?.completed_at;
  const [editing, setEditing] = useState(!done);
  const [form, setForm] = useState(onboarding?.answers || {});
  const [busy, setBusy] = useState(false);
  const L = (i) => i[en ? 1 : 0];

  const save = async () => {
    setBusy(true);
    try {
      await api('/api/tracking/onboarding', { method: 'POST', body: JSON.stringify({ answers: form }) });
      toast.success(en ? 'Onboarding saved' : 'Onboarding enregistré');
      setEditing(false);
      onSaved && onSaved();
    } catch {
      toast.error(en ? 'Save failed' : "Échec de l'enregistrement");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="onboarding-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center gap-2 font-bold text-white"><ClipboardList className="w-4 h-4 text-primary" /> {en ? 'Onboarding questionnaire' : 'Questionnaire d\'onboarding'}</h3>
        {done && !editing && (
          <button onClick={() => { setForm(onboarding.answers || {}); setEditing(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors" data-testid="onboarding-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> {en ? 'Edit' : 'Modifier'}
          </button>
        )}
      </div>

      {!editing && done ? (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="onboarding-summary">
          {FIELDS.map((f) => (
            <div key={f.k} className="rounded-xl bg-zinc-800/40 border border-white/10 p-3">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">{L(f.label)}</p>
              <p className="text-sm text-white mt-0.5 whitespace-pre-wrap">{onboarding.answers?.[f.k] || '—'}</p>
            </div>
          ))}
        </div>
      ) : (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {!done && <p className="text-sm text-zinc-400 mb-2">{en ? 'Help your coach tailor your program by filling this in 👇' : 'Aidez votre coach à personnaliser votre suivi en remplissant ceci 👇'}</p>}
            {FIELDS.map((f) => (
              <div key={f.k}>
                <label className="text-xs text-zinc-400 mb-1 block">{L(f.label)}</label>
                {f.type === 'select' ? (
                  <select value={form[f.k] || ''} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid={`onboarding-${f.k}`}>
                    <option value="">—</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea rows={2} value={form[f.k] || ''} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid={`onboarding-${f.k}`} />
                ) : (
                  <input value={form[f.k] || ''} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none" data-testid={`onboarding-${f.k}`} />
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <button onClick={save} disabled={busy} className="flex-1 py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="onboarding-save-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Save' : 'Enregistrer'}
              </button>
              {done && <button onClick={() => setEditing(false)} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-zinc-300 border border-white/10 hover:text-white transition-colors">{en ? 'Cancel' : 'Annuler'}</button>}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default OnboardingCard;
